import React, { useEffect, useRef, useState } from 'react';
import type { GameId } from '../../api/types';
import { GAME_LIST, type GameDefinition } from '../../data/games';
import { useChanceyTheme } from '../../theme/ThemeContext';
import { Icon } from './Icons';

const GAME_PRESENTATION: Record<GameId, { initial: string; bg: string; fg: string }> = {
  'mega-millions': { initial: 'M', bg: '#F5B82E', fg: '#1B1410' },
  powerball: { initial: 'P', bg: '#E8375C', fg: '#FFFFFF' },
};

function gamePresentation(gameId: GameId): { initial: string; bg: string; fg: string } {
  return GAME_PRESENTATION[gameId];
}

function GameMark({ game, size = 24 }: { game: GameDefinition; size?: number }) {
  const { typ } = useChanceyTheme();
  const p = gamePresentation(game.id);
  return (
    <span
      className="relative flex shrink-0 items-center justify-center overflow-hidden rounded-full"
      style={{
        width: size,
        height: size,
        background: p.bg,
        color: p.fg,
        fontFamily: typ.display,
        fontWeight: 700,
        fontSize: Math.max(11, Math.round(size * 0.48)),
        boxShadow: `inset 0 -${Math.max(2, size * 0.12)}px ${Math.max(4, size * 0.2)}px rgba(0,0,0,0.22), inset 0 ${Math.max(2, size * 0.1)}px ${Math.max(3, size * 0.15)}px rgba(255,255,255,0.35)`,
      }}
      aria-hidden
    >
      <span
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0) 56%)',
        }}
      />
      <span className="relative z-10">{p.initial}</span>
    </span>
  );
}

export function GameBadge({ game }: { game: GameDefinition }) {
  const { c, typ } = useChanceyTheme();
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1"
      style={{ background: c.surfaceAlt, borderColor: c.line }}
      aria-label={`Active game: ${game.name}`}
    >
      <GameMark game={game} size={20} />
      <span
        className="text-[11px] font-bold uppercase tracking-[0.06em]"
        style={{ color: c.ink, fontFamily: typ.body }}
      >
        {game.name}
      </span>
    </div>
  );
}

export function GameSwitcher({
  game,
  onSelectGame,
}: {
  game: GameDefinition;
  onSelectGame: (gameId: GameId) => void;
}) {
  const { typ } = useChanceyTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      const container = containerRef.current;
      if (container && event.target instanceof Node && !container.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative z-20 w-full">
      <button
        type="button"
        className="flex w-full items-center gap-3 rounded-[14px] px-2 py-2 text-left"
        style={{
          border: '0.5px solid rgba(255,255,255,0.5)',
          background: 'rgba(255,255,255,0.05)',
          color: '#fff',
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Active game: ${game.name}. Switch game`}
        onClick={() => setOpen((v) => !v)}
      >
        <GameMark game={game} size={32} />
        <span className="min-w-0 flex-1">
          <span
            className="block text-[9px] font-bold uppercase tracking-[0.14em] opacity-80"
            style={{ fontFamily: typ.body }}
          >
            Playing
          </span>
          <span
            className="mt-px block truncate text-[17px] leading-tight"
            style={{ fontFamily: typ.display, fontWeight: typ.displayWeight, letterSpacing: typ.displayLetter }}
          >
            {game.name}
          </span>
        </span>
        <span
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em]"
          style={{ background: 'rgba(255,255,255,0.22)', fontFamily: typ.body }}
        >
          Switch
          <span
            className="inline-flex leading-none transition-transform"
            style={{ transform: open ? 'rotate(180deg)' : undefined }}
            aria-hidden
          >
            {Icon.chevronDown(12, 'currentColor')}
          </span>
        </span>
      </button>

      {open ? (
        <div
          className="absolute left-0 right-0 top-full z-30 mt-2 rounded-[16px] border p-1.5"
          style={{
            borderColor: 'rgba(255,255,255,0.24)',
            background: 'rgba(27,20,16,0.28)',
            backdropFilter: 'blur(18px)',
            boxShadow: '0 16px 42px rgba(0,0,0,0.28)',
          }}
          role="listbox"
          aria-label="Switch active game"
        >
          {GAME_LIST.map((g) => {
            const active = g.id === game.id;
            return (
              <button
                key={g.id}
                type="button"
                className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left"
                style={{
                  background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
                  color: '#fff',
                }}
                role="option"
                aria-selected={active}
                onClick={() => {
                  onSelectGame(g.id);
                  setOpen(false);
                }}
              >
                <GameMark game={g} size={28} />
                <span className="min-w-0 flex-1">
                  <span
                    className="block text-[13px] font-semibold leading-tight"
                    style={{ fontFamily: typ.display, letterSpacing: typ.displayLetter }}
                  >
                    {g.name}
                  </span>
                  <span className="mt-0.5 block text-[10px] leading-snug opacity-75" style={{ fontFamily: typ.body }}>
                    {g.mainCount} of {g.mainRange} + {g.bonusLabel}
                  </span>
                </span>
                {active ? (
                  <span
                    className="rounded px-1.5 py-px text-[8px] font-extrabold uppercase tracking-[0.08em]"
                    style={{ background: gamePresentation(g.id).bg, color: gamePresentation(g.id).fg, fontFamily: typ.body }}
                  >
                    Active
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
